import { View, ViewModel } from "../..";
import { RawView } from "../../uiPatterns/mvvm/view";
import { SamePropertyNamesBinder } from "../bridges/utilities";
import { BindingPrototype, BindingPrototypeParser } from "./bindingPrototype";
import { HtmlBridgePrototype } from "./htmlBridgePrototype";
import { HtmlBridgePrototypeFactory } from "./htmlBridgePrototypeFactory";
import { NodeFlattener } from "./nodeFlatenner";

export class HtmlStringParser{
    public parse(htmlString: string,rootId:string): Node {
        let rootElement = this.readFromString(htmlString,rootId);
        return rootElement;
    }

    private readFromString(htmlString: string,rootId:string): Node {
        let domParser = new DOMParser();
        let document = domParser.parseFromString(htmlString, "text/html");
        let element : Node = null;
        if(rootId){
            const foundElement =this.loadElementsById(document,rootId);
            element = foundElement;
        }else{
            const html = document.children[0];
            const body = html.children[html.children.length - 1];
            if(body.childElementCount>0)
                element = body.children[0];
        }
        return element;
    }

    private loadElementsById(document : HTMLDocument,rootId:string):Element{
        let queue = [...document.children];
        while(queue.length>0){
            let current = queue.shift();
            if(current.id===rootId)
                return current;
            queue.push(...current.children);
        }
        return null;
    }
}
class BindedProperty{
    constructor(attribute:string,bindingPrototype:BindingPrototype) {
        this.attribute = attribute;
        this.bindingPrototype = bindingPrototype;
    }
    public attribute:string;
    public bindingPrototype:BindingPrototype;
}
class BindingAnalyzer{
    private GetTextNodeBindings(node:Node):BindedProperty[]{
        let value = node.textContent;
        var bindingPrototype = BindingPrototypeParser.ParseBindingNoImplicitBindings(value);
        if(bindingPrototype!=null){
            node.textContent = '';
            return [new BindedProperty('TextNodeTextContent',bindingPrototype)];
        }
        return [];
    }
    private moveTemplateSelectorsToFirst(attributes:string[]):string[]{
        let elements = attributes.filter(x=>x=='data-b-comp-itemtemplateselector' || x=='data-b-comp-templateselectorfunction').sort((a,b)=>attributes.indexOf(a)-attributes.indexOf(b));
        elements.forEach(element => {
            let index = attributes.indexOf(element);
            attributes.unshift(...attributes.splice(index,1));
        });
        return attributes;
    }
    private moveImportantsToFirst(attributes:string[]):string[]{
        let elements = attributes.filter(x=>x.startsWith('data-b-comp-important')).sort((a,b)=>attributes.indexOf(a)-attributes.indexOf(b));
        elements.forEach(element => {
            let index = attributes.indexOf(element);
            attributes.unshift(...attributes.splice(index,1));
        });
        return attributes;
    }
    private moveComponentsToFirst(attributes:string[]):string[]{
        let componentIndex =attributes.indexOf('data-b-component'); 
        if(componentIndex>0){
            attributes.unshift(...attributes.splice(componentIndex,1));
        }
        return attributes;
    }
    private GetElementNodeBindings(element:Element):BindedProperty[]{
        let attributes = element.getAttributeNames().filter(x=>x.startsWith('data-b-')).sort();
        attributes = this.moveTemplateSelectorsToFirst(attributes);
        attributes = this.moveImportantsToFirst(attributes);
        attributes = this.moveComponentsToFirst(attributes);
        let result : BindedProperty[]=[];
        attributes.forEach(attributeName => {
            let attributeValue = element.getAttribute(attributeName).trim();
            let bindingPrototype = BindingPrototypeParser.ParseBinding(attributeValue);
            if(bindingPrototype!=null)
                element.removeAttribute(attributeName);
            result.push(new BindedProperty(attributeName.substring(7),bindingPrototype))
        });
        return result;
    }
    private GetSingleNodeBindings(node:Node):BindedProperty[]{
        try{
            if(node.nodeType==Node.TEXT_NODE){
                return this.GetTextNodeBindings(node)
            }else if(node.nodeType==Node.ELEMENT_NODE){
                return this.GetElementNodeBindings(node as Element);
            }else{
                return [];
            }
        }
        catch(ex){
            console.log(node);
            throw ex;
        }
    }

    public GetBindings(nodes:Node[]):BindedProperty[][]{
        let result : BindedProperty[][] = [];
        for(let i=0;i<nodes.length;i++){
            result.push(this.GetSingleNodeBindings(nodes[i]));
        }
        return result;
    }
}


class PrototypeCreator {
    private static mapping:Map<string,Prototype> = new Map<string,Prototype>();
    private static generatePrototype(root:Node,flattedNodes:Node[],bindings:BindedProperty[][]):Prototype{
        let prototypeFactory = new HtmlBridgePrototypeFactory();
        let prototype = new Prototype();
        prototype.root = root;
        prototype.setElements(flattedNodes);
        for(let i=0;i<flattedNodes.length;i++){
            let node = flattedNodes[i];
            let nodeBindings = bindings[i];
            let bridges = nodeBindings.map(x=>prototypeFactory.getPrototype(node,x.attribute,x.bindingPrototype));
            prototype.setNodeBridges(i,bridges);    
        }
        return prototype;
    }
    public static GetPrototype(view:View<ViewModel>):Prototype{
        if(this.mapping.has(view.GetViewUniqueId())==false){
            let prototype =PrototypeCreator.createPrototype(view.loadHtml(),view.GetViewUniqueId());
            this.mapping.set(view.GetViewUniqueId(),prototype);
        }
        return this.mapping.get(view.GetViewUniqueId());
    } 

    public static createPrototype(html:string,viewUniqueId:string=null):Prototype{
        let root = new HtmlStringParser().parse(html,viewUniqueId);
        let flattedNodes = NodeFlattener.flattenNodes(root);
        let bindings = new BindingAnalyzer().GetBindings(flattedNodes);
        let prototype = this.generatePrototype(root,flattedNodes,bindings);
        return prototype;
    }
}
class DummyViewModel extends ViewModel{
    public getTypeId(): string {
        throw new Error("Method not implemented.");
    }
    public GetDefaultView(): View<ViewModel> {
        throw new Error("Method not implemented.");
    }
}
class Prototype{
    public root:Node;
    public elements:Node[];
    public bridges:Map<number, HtmlBridgePrototype[]>;
    private invalidPropertiesAndMethods:Set<string>;
    constructor() {
        this.elements = [];
        this.bridges = new Map<number,HtmlBridgePrototype[]>();        
        let dummy = new DummyViewModel(null);
        this.invalidPropertiesAndMethods = new Set<string>();
        let items = []
        items.push(...this.getMethods(dummy));
        items.push(...this.getAllEntries(dummy));
        items.forEach(x=>this.invalidPropertiesAndMethods.add(x));
    }

    setElements(flattedNodes: Node[]) {
        this.elements = flattedNodes;
        this.elements.forEach((x,i)=>(<any>x).__BinderIndex__=i);
        this.elements.forEach((x,i)=>(<any>x).__BinderPrototype__=this);
    }
    
    

    setNodeBridges(elementIndex:number, bridges: HtmlBridgePrototype[]) {
        if(bridges.length>0)
            this.bridges.set(elementIndex,bridges);
    }
    public createFromTemplateNode(viewModel:ViewModel,node:Node):Node{
        let index = (<any>node).__BinderIndex__;
        return this.createFromIndex(viewModel,index);
    }
    private getMethods(viewModel:object){
        let properties = new Set<string>()
        let currentObj = viewModel;
        do {
            Object.getOwnPropertyNames(currentObj).map(item => properties.add(item))
        } while ((currentObj = Object.getPrototypeOf(currentObj)));
        return [...properties.keys()].filter(item => typeof viewModel[item] === 'function' && this.invalidPropertiesAndMethods.has(item)==false)
    }

    private getAllEntries(obj:any):Set<string>{
        let result : any[] = [];
        let o = obj;
        while((o=Object.getPrototypeOf(o))){
            let names = Object.getOwnPropertyNames(o);
            let desc = Object.getOwnPropertyDescriptors(o);
            let map = names.map(x=>[x,desc[x]]);
            result.push(...map.filter(x=>x[1]['value']===undefined && (<any>x[0]).startsWith('__')==false && this.invalidPropertiesAndMethods.has(<any>x[0])==false))
        }
        return new Set(result.map(x=>x[0]));
    }

    private createNewViewForViewModel(viewModel:ViewModel):View<ViewModel>{
        let rawView = new RawView();
        rawView.dataContext = viewModel;
        let methods = this.getMethods(viewModel);
        let properties = this.getAllEntries(viewModel);
        properties.forEach(element => {
            if(rawView[element]==undefined){
                rawView[`_${element}`]=null;
                Object.defineProperty(rawView,element,{
                    set:(function(x){this[`_${element}`]=x;}).bind(rawView),
                    get:(function(){return this[`_${element}`];}),
                    configurable:true
                });
            }
        });
        methods.forEach(element=>{
            rawView[element]=(function(...args){
                return this["dataContext"][element](...args);
            }).bind(rawView);
        });
        return rawView;
    }

    public createFromIndex(viewModel: ViewModel,index:number):Node{
        let templateRoot = this.elements[index];
        let clonedRoot = templateRoot.cloneNode(true);
        if(clonedRoot instanceof Element){
            clonedRoot.setAttribute('viewModelObjectId',viewModel.viewModelObjectId.toString());
        }
        let templateNodes = NodeFlattener.flattenNodes(templateRoot);
        let flattedItems = NodeFlattener.flattenNodes(clonedRoot);
        let view = this.createNewViewForViewModel(viewModel);
        SamePropertyNamesBinder.bindSourceAndDestination(viewModel,view);

        for(let i=0;i<flattedItems.length;i++){
            let element = flattedItems[i];
            let realIndex = (<any>templateNodes[i]).__BinderIndex__;
            (<any>element).binderRelatedView = view;
            (<any>element).__BinderIndex__=realIndex;
            (<any>element).__BinderPrototype__=this;
        }
        let removedElements : Set<Node> = new Set<Node>(); 
        for(let i=0;i<flattedItems.length;i++){
            let element = flattedItems[i];
            let realIndex = (<any>templateNodes[i]).__BinderIndex__;

            if(element['isRemoved']===true){
                let allRemoved = NodeFlattener.flattenNodes(element);
                allRemoved.forEach(x=>removedElements.add(x))
                element['isRemoved']=false;
            }
            if(removedElements.has(element)==false){
                if(this.bridges.has(realIndex)){
                    let nodeBridges = this.bridges.get(realIndex);
                    nodeBridges.forEach(x=>x.create(view,element)); 
                }
            }
        }
        return clonedRoot as Element;
    }

    public create(view: View<ViewModel>):Node {
        let clonedRoot = this.root.cloneNode(true);
        if(clonedRoot instanceof Element){
            clonedRoot.setAttribute('viewModelObjectId',view.dataContext.viewModelObjectId.toString());
        }
        let flattedItems = NodeFlattener.flattenNodes(clonedRoot);
        for(let i=0;i<flattedItems.length;i++){
            let element = flattedItems[i];
            (<any>element).binderRelatedView = view;
            (<any>element).__BinderIndex__=i;
            (<any>element).__BinderPrototype__=this;
        }       
        let removedElements : Set<Node> = new Set<Node>(); 
        for(let i=0;i<flattedItems.length;i++){
            let element = flattedItems[i];
            if(element['isRemoved']===true){
                let allRemoved = NodeFlattener.flattenNodes(element);
                allRemoved.forEach(x=>removedElements.add(x))
                element['isRemoved']=false;
            }
            if(removedElements.has(element)==false){
                if(this.bridges.has(i)){
                    let nodeBridges = this.bridges.get(i);
                    nodeBridges.forEach(x=>x.create(view,element)); 
                }
            }
        }
        return clonedRoot as Element;
    }
}
export {Prototype,PrototypeCreator}